import React, { FC, useState } from 'react';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import AccordionWidget from 'app/modules/posts/components/AccordionWidget';
import AccordionWidgetTitle from 'app/modules/posts/components/accordionWidget/AccordionWidgetTitle';
import { Button, Chip, Icon } from '@kontentino/ui';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';
import clsx from 'clsx';
import PostOverviewLocalPostsList from 'app/modules/posts/modules/post/components/overview/postOverviewLocalPosts/PostOverviewLocalPostsList';
import { usePostQuery } from 'app/modules/posts/modules/post/hooks/usePostQuery';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import PostOverviewLocalPostsSelectProfilesModal from 'app/modules/posts/modules/post/components/overview/postOverviewLocalPosts/PostOverviewLocalPostsSelectProfilesModal';
import NumberUtil from 'utils/number';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import { useTranslation } from 'react-i18next';

export type Props = {
  id: number;
  accordionProps?: Partial<React.ComponentProps<typeof AccordionWidget>>;
};

const PostOverviewLocalPosts: FC<Props> = ({ id, accordionProps }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { postDetail } = usePostQuery(id);
  const { t } = useTranslation();
  const postWasDistributed =
    (postDetail.data?.localPosts?.data?.length ?? 0) > 0;

  if (postDetail.data?.localPosts?.data) {
    return (
      <AccordionWidget
        icon={<AccordionWidgetIcon variant="localPosts" />}
        titleSuffix={(isOpen) =>
          !isOpen &&
          !!postDetail.data.localPosts?.data.length && (
            <Chip variant="darkGrey">
              {NumberUtil.compact(postDetail.data.localPosts.data.length)}
            </Chip>
          )
        }
        title={
          <AccordionWidgetTitle
            variant="localPosts"
            className="tw-flex tw-justify-between"
          />
        }
        className="tw-flex tw-flex-col"
        data-cy="post-preview_local-posts"
        {...accordionProps}
      >
        <div className="tw-flex tw-flex-col tw-divide-y tw-divide-grayscale-20 tw-rounded-md tw-bg-grayscale-5 tw-p-4 tw-text-sm">
          <div className="tw-max-h-[350px] tw-overflow-y-auto">
            <PostOverviewLocalPostsList
              localPosts={postDetail.data.localPosts.data}
              globalPostId={postDetail.data.id}
            />
          </div>

          <div className="tw-flex tw-items-center">
            <UserPermissionGate
              scopes={UserPermission.GLOBAL_PAGE_ASSIGN_CHANNEL}
            >
              <Button
                variant="ghost"
                onClick={() => setIsOpen(true)}
                className="tw-mt-2 tw-h-auto !tw-p-0 tw-text-sm tw-text-primary-100"
                iconBefore={<Icon icon={faPlus} />}
                data-cy="post-add-new-local-profile-button"
              >
                <span>{t('globalPages.addNewLocalProfile')}</span>
              </Button>
            </UserPermissionGate>

            <PostOverviewLocalPostsSelectProfilesModal
              post={postDetail.data}
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
            />
          </div>
        </div>

        <div
          className={clsx(
            'tw-mt-3 tw-flex tw-h-full tw-items-center tw-gap-x-1 tw-text-grayscale-100',
          )}
        >
          <Icon icon={faInfoCircle} />
          <span className="tw-text-sm tw-font-regular">
            {postWasDistributed
              ? t('globalPages.distributedPostAcrossLocalProfiles')
              : t('globalPages.notDistributedPostAcrossLocalProfiles')}
          </span>
        </div>
      </AccordionWidget>
    );
  }

  return null;
};

export default PostOverviewLocalPosts;
